import type { App } from "@/services/fobizzToolsApi";
import { AppEnum } from "@/services/fobizzToolsApi";
import type { ToolGroup } from "@/types/tools";
import { useI18n } from "vue-i18n";

/**
 * Composable for managing tool groups and their configurations.
 *
 * IMPORTANT: The order of tools within each group is carefully designed for optimal UX.
 * The toolOrder arrays define the exact sequence in which tools should appear in the UI.
 * Do not modify the order without careful consideration and team discussion.
 *
 * Group Structure:
 * 1. AI Chat and Assistants (aiChat)
 *    - Core chat tools first, followed by assistant management
 * 2. AI Feedback (aiFeedback)
 *    - Experimental features for automated feedback
 * 3. Multimedia Creation (multimedia)
 *    - Tools ordered by complexity and frequency of use
 * 4. Material Creation (material)
 *    - Core worksheet tools first, followed by specialized creation tools
 * 5. Sharing Tools (share)
 *    - Recording tools grouped first, followed by distribution tools
 *
 * @returns {Object} Functions for accessing and managing tool groups
 */
export function useToolGroups() {
  const { t } = useI18n();

  // Tool groups with carefully maintained ordering
  // WARNING: Maintaining this exact order is critical for UI consistency
  // If you need to add new tools, please add them to the appropriate group
  // while preserving the existing order of other tools
  const groups: ToolGroup[] = [
    {
      id: "aiChat",
      title: t("startPage.headers.sectionChatTools"),
      toolOrder: [
        { type: "backend", id: AppEnum.TOOLS_AI_CONVERSATION },
        { type: "backend", id: AppEnum.TOOLS_AI_PERSONA },
        { type: "backend", id: AppEnum.TOOLS_AI_DOCUMENT_QA },
        { type: "manual", key: "assistant" },
        { type: "manual", key: "myAssistants" },
        { type: "manual", key: "miniTool" },
      ],
    },
    {
      id: "aiFeedback",
      title: t("startPage.headers.sectionFeedbackTools"),
      badge: t("labels.experimental"),
      toolOrder: [
        { type: "backend", id: AppEnum.TOOLS_AI_FEEDBACK },
        { type: "backend", id: AppEnum.TOOLS_PUPIL_FEEDBACK_ASSIGNMENT },
      ],
    },
    {
      id: "multimedia",
      title: t("startPage.headers.sectionMultimediaTools"),
      badge: t("labels.new"),
      toolOrder: [
        { type: "manual", key: "podcast" },
        { type: "backend", id: AppEnum.TOOLS_AI_IMAGE },
        { type: "manual", key: "text" },
        { type: "manual", key: "lecture" },
        { type: "manual", key: "audio" },
        { type: "manual", key: "transcript" },
      ],
    },
    {
      id: "material",
      title: t("startPage.headers.sectionMaterialTools"),
      toolOrder: [
        { type: "backend", id: AppEnum.TOOLS_WORKSHEET_WORKSHEET_WIZARD },
        { type: "backend", id: AppEnum.TOOLS_WORKSHEET_WORKSHEET },
        { type: "backend", id: AppEnum.TOOLS_CLASSROOM_SCREEN_SCREEN },
        { type: "backend", id: AppEnum.TOOLS_PINBOARD_BOARD },
        { type: "backend", id: AppEnum.TOOLS_SURVEYS_SURVEY },
        { type: "backend", id: AppEnum.TOOLS_TEXT_EDITOR_DOCUMENT },
        { type: "backend", id: AppEnum.TOOLS_WEBSITE_PAGE },
        { type: "backend", id: AppEnum.TOOLS_WORD_CLOUDS_WORD_CLOUD },
      ],
    },
    {
      id: "share",
      title: t("startPage.headers.sectionShareTools"),
      toolOrder: [
        { type: "backend", id: AppEnum.TOOLS_VIDEO_RECORDER_FILE },
        { type: "backend", id: AppEnum.TOOLS_AUDIO_RECORDER_FILE },
        { type: "backend", id: AppEnum.TOOLS_SCREEN_RECORDER_FILE },
        { type: "backend", id: AppEnum.TOOLS_LINK_SHORTENER_LINK },
        { type: "backend", id: AppEnum.TOOLS_FILE_SHARING_FILE },
        { type: "backend", id: AppEnum.TOOLS_QRCODES_QRCODE },
      ],
    },
  ];

  /**
   * Check if an app belongs to a specific group
   */
  const isInGroup = (app: App, groupId: string): boolean => {
    const group = groups.find((g) => g.id === groupId);
    return group
      ? group.toolOrder.some(
          (item) => item.type === "backend" && item.id === app.id
        )
      : false;
  };

  /**
   * Get all apps for a specific group
   */
  const getGroupApps = (groupId: string): AppEnum[] => {
    const group = groups.find((g) => g.id === groupId);
    return group
      ? group.toolOrder
          .filter((item) => item.type === "backend")
          .map((item) => item.id)
      : [];
  };

  /**
   * Get group configuration by ID
   */
  const getGroup = (groupId: string): ToolGroup | undefined =>
    groups.find((g) => g.id === groupId);

  /**
   * Get all group configurations
   */
  const getAllGroups = (): ToolGroup[] => groups;

  return {
    isInGroup,
    getGroupApps,
    getGroup,
    getAllGroups,
  };
}
